<template>
    <BlocksCLHero v-if="display.component === 'hero'" v-bind="assignedProps" @add-product="addProduct" />
    <BlocksCLAccordion v-else-if="display.component === 'accordion'" v-bind="assignedProps" />
    <BlocksCLText v-else-if="display.component === 'plain-text'" v-bind="assignedProps" />
    <BlocksCLBanner v-else-if="display.component === 'banner'" v-bind="assignedProps" @add-product="addProduct" />
    <BlocksCLSlider v-else-if="display.component === 'slider'" v-bind="assignedProps" />
    <BlocksCLLunchMenu v-else-if="display.component === 'lunch'" v-bind="assignedProps" />
    <BlocksCLCards
        v-else-if="display.component === 'cards'"
        :count-override="display.count"
        v-bind="assignedProps"
        @add-product="addProduct"
    >
        <template v-for="(_, name) in $slots" #[name]="slotData">
            <slot :name="name" v-bind="slotData || {}" />
        </template>
    </BlocksCLCards>
    <BlocksCLQuickLinks v-else-if="['icon-cards', 'quick-links'].includes(display.component)" v-bind="assignedProps" />
    <BlocksCLFoodMenu v-else-if="display.component === 'foodMenu'" class="block" v-bind="assignedProps" />
    <BlocksCLEmbedded v-else-if="display.component === 'embedded'" v-bind="assignedProps" />
    <BlocksCLKeyFigures v-else-if="display.component === 'key-figures'" v-bind="assignedProps" />
    <BlocksCLList v-else-if="display.component === 'contentlist'" v-bind="assignedProps" />
    <BlocksCLProduct v-else-if="display.component === 'product'" v-bind="assignedProps" @add-product="addProduct" />
    <BlocksCLDefault v-else-if="['half-content', 'list'].includes(display.component)" v-bind="assignedProps" />
    <BlocksDefaultContent v-else-if="display.component !== 'redirect'" v-bind="assignedProps" />
</template>
<script setup>
import { useNuxtApp, useI18n, computed, useAttrs, storeToRefs, useAnalytics } from '#imports';
import { useUserStore } from '~dsc/stores/cms/user';

const { $analytics, $paths } = useNuxtApp();
const { loggedIn } = storeToRefs(useUserStore());
const { gtm } = useAnalytics();
const attrs = useAttrs();

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
    id: {
        type: String,
        required: true,
        default: '',
    },
    contentType: {
        type: [String, null],
        required: false,
        default: 'cardList',
    },
    internal: {
        type: [String, null],
        required: false,
        default: 'Card List',
    },
    display: {
        type: [Object],
        required: false,
        default: () => ({
            component: 'hero',
        }),
    },
    title: {
        type: [String, null],
        required: false,
        default: '',
    },
    cards: {
        type: [Array],
        required: false,
        default: () => [],
    },
});

const i18n = useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            more: 'Lue lisää',
        },
        'en-EU': {
            more: 'Read more',
        },
        'sv-SE': {
            more: 'Läs mer',
        },
        'no-NO': {
            more: 'Les mer',
        },
    },
});

const assignedProps = computed(() => {
    if (props.display.component === 'foodMenu') {
        return {
            ...attrs,
            ...props,
            menu: props.cards,
        };
    }

    return {
        ...attrs,
        ...props,
        cards: props.cards.filter(card => card.contentType !== 'helmisimpukkaProductMenu').map(card => {
            if (card.contentType === 'articlePage') {
                return Object.assign({}, card, {
                    links: [{
                        url: {
                            contentType: 'link',
                            type: 'internal',
                            url: `${$paths.getPath('articles').replace(/^\//, '')}/${card.slug}`,
                        },
                        title: i18n.t('more'),
                    }],
                    text: card.description,
                });
            }

            return card;
        }),
    };
});

function addProduct(product) {
    gtm().trackEvent({
        event: 'select_item',
        action: 'click',
        item_list_id: props.id,
        item_list_name: props.title && props.title !== '' ? props.title : props.internal,
        items: [$analytics.productToAnalytics(product)],
        event_function: 'general',
        logged_in_status: loggedIn.value ? 'logged_in' : 'logged_out',
        user_type: 'consumer',
    });
}
</script>
